import {Breakpoints} from '@angular/cdk/layout';
import {Component, Inject} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';

import {BehaviorSubject, firstValueFrom, NEVER} from 'rxjs';

import {FormManagerService} from '@sim/form';
import {
  BaseTableComponent,
  makeQueryBuilder,
  makeQueryRules,
  TableField,
  TableFilterType,
  TableSelection,
  TableSortType,
  UiTableComponent,
} from '@ui/ui.module';

import {CardsApiService, CardType, filePath, SwalService} from '@atmi/core';
import {
  Card,
  CardD,
  CardDFilter,
  CardDFiltersMeta,
  CardMI,
  CardMIFilter,
  CardMIFiltersMeta,
  CommonCard,
  RelationDMeta,
  RelationFMeta,
  RelationMIMeta,
  RelationOAMeta,
  RelationSMeta,
} from '@atmi/core/models';

import {CardF, CardFFilter, CardFFiltersMeta} from '@atmi/core/models/cards/card-f';
import {CardOA, CardOAFilter, CardOAFiltersMeta} from '@atmi/core/models/cards/card-oa';
import {CardS, CardSFilter, CardSFiltersMeta} from '@atmi/core/models/cards/card-s';
import {CardDFiltersComponent} from '@atmi/shared/filters/card-d-filters/card-d-filters.component';
import {CardFFiltersComponent} from '@atmi/shared/filters/card-f-filters/card-f-filters.component';
import {CardMiFiltersComponent} from '@atmi/shared/filters/card-mi-filters/card-mi-filters.component';
import {CardOaFiltersComponent} from '@atmi/shared/filters/card-oa-filters/card-oa-filters.component';
import {CardSFiltersComponent} from '@atmi/shared/filters/card-s-filters/card-s-filters.component';
import {first} from 'rxjs/operators';

export interface CardsSourceArcDialogData {
  docArc: string;
  type: CardType;
}

// TODO: Refactor in a shared utils
function getInfoByType(type: CardType) {
  switch (type) {
    case 'oa':
      return {
        filter: CardOaFiltersComponent,
        filterMeta: CardOAFiltersMeta,
        relationMeta: RelationOAMeta,
        property: 'beneOA'
      };
    case 'd':
      return {
        filter: CardDFiltersComponent,
        filterMeta: CardDFiltersMeta,
        relationMeta: RelationDMeta,
        property: 'beneD'
      };
    case 'mi':
      return {
        filter: CardMiFiltersComponent,
        filterMeta: CardMIFiltersMeta,
        relationMeta: RelationMIMeta,
        property: 'beneMI'
      };
    case 'f':
      return {
        filter: CardFFiltersComponent,
        filterMeta: CardFFiltersMeta,
        relationMeta: RelationFMeta,
        property: 'beneF'
      };
    case 's':
      return {
        filter: CardSFiltersComponent,
        filterMeta: CardSFiltersMeta,
        relationMeta: RelationSMeta,
        property: 'beneS'
      };
    default:
      return {
        filter: CardOaFiltersComponent,
        filterMeta: CardOAFiltersMeta,
        relationMeta: RelationOAMeta,
        property: 'beneOA'
      };
  }
}

@Component({
  selector: 'app-cards-source-arc-dialog',
  styleUrls: ['./cards-source-arc-dialog.component.scss'],
  templateUrl: './cards-source-arc-dialog.component.html',
})
export class CardsSourceArcDialogComponent
  extends BaseTableComponent<Card>
  implements UiTableComponent<Card> {

  readonly MobileFiltersComponent = getInfoByType(this.data.type).filter;

  readonly fields: TableField<any>[] = [
    {
      header: '',
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'chekbox',
      selectable: true,
    },
    {
      context: (x) => x.previewUrl ? `${this.fileUrl}${x.previewUrl}` : undefined,
      header: this.translate.instant('Anteprima'),
      key: 'previewUrl',
      template: 'img',
    },
    {
      header: this.translate.instant('Codice unico'),
      key: 'codiceUnico',
      sortable: true,
    },
    {
      context: (x) => x.SGT.SGTT,
      header: this.translate.instant('Titolo soggetto'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'SGT.SGTT',
      sortable: true,
    },
  ];

  readonly filters$ = new BehaviorSubject<TableFilterType | undefined>(undefined);

  readonly pager$ = ((page?: number, limit?: number, sort?: TableSortType, filter?: TableFilterType) =>
      this.getIndexByType(page, limit, sort, filter)
  );
  readonly relationForm: UntypedFormGroup;

  constructor(
    private readonly dialogRef: MatDialogRef<CardsSourceArcDialogComponent>,
    private readonly formManager: FormManagerService,
    private readonly cardsApi: CardsApiService,
    protected readonly translate: TranslateService,
    protected readonly router: Router,
    protected readonly route: ActivatedRoute,
    protected readonly swalService: SwalService,
    @Inject(MAT_DIALOG_DATA) public readonly data: CardsSourceArcDialogData,
    @Inject(filePath) private readonly fileUrl: string,
  ) {
    super(translate, router, route, swalService, `${data.type}/manager`, 'local');

    const relationMeta = getInfoByType(this.data.type).relationMeta;
    this.relationForm = this.formManager.makeForm(relationMeta);
  }

  selectHandler(selectedCard: TableSelection<Card> | Event) {
    if (selectedCard instanceof Event) {
      return;
    }

    super.selectHandler(selectedCard);
    if (selectedCard.selected.length > 0) {
      const property = getInfoByType(this.data.type).property;

      this.relationForm.controls[property].setValue(selectedCard.selected[0].id);
    }
  }

  async completeHandler() {
    if (this.relationForm.valid) {
      const property = getInfoByType(this.data.type).property;

      const card = await firstValueFrom(this.getByType(this.relationForm.value[property]).pipe(first()));
      const lastOrder = card.fontiArc.reduce((prev, curr) => curr.ordine > prev ? curr.ordine : prev, 0);
      const cardToUpdate: CommonCard = {
        ...card,
        fontiArc: [...card.fontiArc, {ordine: lastOrder + 1, docArc: this.data.docArc}],
      };
      const cardUpdated = await firstValueFrom(this.updateByType(cardToUpdate).pipe(first()));
      this.dialogRef.close(cardUpdated);
    } else {
      this.relationForm.markAllAsTouched();
    }
  }

  closeHandler() {
    this.dialogRef.close();
  }

  deleteConcreteHandler() {
    return NEVER;
  }

  deleteSelectedConcreteHandler() {
    return NEVER;
  }

  applyFilterWithDelayHandler(filters: CardOAFilter | CardFFilter | CardMIFilter | CardDFilter | CardSFilter) {
    const rules = makeQueryRules(filters, getInfoByType(this.data.type).filterMeta);
    const query = makeQueryBuilder(rules);

    this.filters$.next(query);
  }

  private getIndexByType(page?: number, limit?: number, sort?: TableSortType, filter?: TableFilterType) {
    switch (this.data.type) {
      case 'oa':
        return this.cardsApi.indexOA({sort, page, limit, filter});
      case 'd':
        return this.cardsApi.indexD({sort, page, limit, filter});
      case 'mi':
        return this.cardsApi.indexMI({sort, page, limit, filter});
      case 'f':
        return this.cardsApi.indexF({sort, page, limit, filter});
      case 's':
        return this.cardsApi.indexS({sort, page, limit, filter});
      default:
        return this.cardsApi.indexOA({sort, page, limit, filter});
    }
  }

  private getByType(id: string) {
    switch (this.data.type) {
      case 'oa':
        return this.cardsApi.getOA(id);
      case 'd':
        return this.cardsApi.getD(id);
      case 'mi':
        return this.cardsApi.getMI(id);
      case 'f':
        return this.cardsApi.getF(id);
      case 's':
        return this.cardsApi.getS(id);
      default:
        return this.cardsApi.getOA(id);
    }
  }

  private updateByType(card: CommonCard) {
    switch (this.data.type) {
      case 'oa':
        return this.cardsApi.putOA(card as CardOA);
      case 'd':
        return this.cardsApi.putD(card as CardD, 'd');
      case 'mi':
        return this.cardsApi.put(card as CardMI, 'mi');
      case 'f':
        return this.cardsApi.putF(card as CardF, 'f');
      case 's':
        return this.cardsApi.putS(card as CardS, 's');
      default:
        return this.cardsApi.putOA(card as CardOA);
    }
  }
}
